/* eslint-disable @typescript-eslint/no-explicit-any */
import { effect, inject, Injectable } from '@angular/core';
import type { newTracker } from '@snowplow/browser-tracker';
import { UnleashClient } from 'unleash-proxy-client';
import { environment } from '../../environments/environment';
import { AuthBridgeService } from './auth-bridge';
import { isFinnetTheme } from './theme';
import { AppVersionService } from './app-version';
import { AnalyticsEvent } from '../analytics/type';
import { PageTitleService } from './page-title';

export interface ConsentData {
  all_purpose_consent: boolean;
  all_vendor_consent: boolean;
  // AI-602: a purpose grant isn't a vendor grant, so purpose_consent can't safely stand in
  // for "did they consent to Snowplow specifically" — see services/CLAUDE.md.
  purpose_consent?: Record<string, unknown>;
  purpose_leg_int?: Record<string, unknown>;
  // AI-602/AI-662: the parent CMP's Snowplow vendor grant — see services/CLAUDE.md.
  snowplow_vendor_consent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SnowplowService {
  private initialized = false;
  private isTrackerActive = false;
  private readonly authBridge = inject(AuthBridgeService);
  private readonly appVersion = inject(AppVersionService);
  private readonly unleash = inject(UnleashClient);
  private readonly pageTitle = inject(PageTitleService);

  /** Tracker API populated after the first dynamic import */
  private trackerModule: typeof import('@snowplow/browser-tracker') | null = null;

  /** Callers of trackBeforeLeaving() waiting for the collector to answer the next request. */
  private readonly requestSettledWaiters: (() => void)[] = [];

  /** Promise that resolves when tracker is ready */
  private trackerReadyResolver!: () => void;
  private readonly trackerReadyPromise: Promise<void> = new Promise<void>((resolve) => {
    this.trackerReadyResolver = resolve;
  });

  /** Initialize Snowplow tracker respecting user consent */
  init() {
    if (this.initialized) return;

    effect(() => {
      const params = new URLSearchParams(globalThis.location.search);
      const consentOverride = params.get('consent') === 'true';
      const consent = consentOverride ? true : this.authBridge.consent();
      const token = this.authBridge.token();

      if (consent !== undefined && !this.initialized) {
        this.initialized = true;
        void this.setupTracker(consent, token).then(
          () => {
            this.trackerReadyResolver();
          },
          (err) => {
            this.initialized = false; // allow retry on next signal change
            this.trackerReadyResolver(); // unblock any code awaiting trackerReady()
            console.error('[Snowplow] Failed to set up tracker:', err);
          },
        );
      }
    });
  }

  trackerReady(): Promise<void> {
    return this.trackerReadyPromise;
  }

  protected isFinnetMode(): boolean {
    const params = new URLSearchParams(globalThis.location.search);
    return isFinnetTheme(params.get('theme'));
  }

  private isWebView(): boolean {
    const ua = navigator.userAgent || navigator.vendor;

    const isIOSWebView = /iPhone|iPad|iPod/.test(ua) && !/Safari/.test(ua);
    const isAndroidWebView = /Android/.test(ua) && /wv/.test(ua);

    return isIOSWebView || isAndroidWebView;
  }

  private buildSpaPath(): string {
    const params = new URLSearchParams(location.search);
    const isin = params.get('isin');
    const suffix = isin ? `zero-ai/${isin}` : 'zero-ai';

    return document.referrer + suffix;
  }

  // Protected so tests can spy on it — @snowplow/browser-tracker exports are non-writable ES module getters
  protected callNewTracker(...args: Parameters<typeof newTracker>): void {
    this.trackerModule!.newTracker(...args);
  }

  // Protected so tests can spy on it — @snowplow/browser-tracker exports are non-writable ES module getters
  protected callSetUserId(userId: string): void {
    this.trackerModule!.setUserId(userId);
  }

  // Protected so tests can override without duplicating the truthy/shape checks.
  protected hasConsented(consent: any): boolean {
    if (consent === true) return true;
    if (this.isConsentObject(consent)) {
      return consent.all_purpose_consent === true || consent.snowplow_vendor_consent === true;
    }
    return false;
  }

  // Excludes the boolean `true` from the `?consent=true` QA override, which isn't a real ConsentData object.
  private isConsentObject(consent: any): consent is ConsentData {
    return !!consent && typeof consent === 'object';
  }

  private async setupTracker(consent: any, token: string | null): Promise<void> {
    const [
      trackerModule,
      { enableLinkClickTracking },
      { PerformanceNavigationTimingPlugin },
      { ClientHintsPlugin },
    ] = await Promise.all([
      import('@snowplow/browser-tracker'),
      import('@snowplow/browser-plugin-link-click-tracking'),
      import('@snowplow/browser-plugin-performance-navigation-timing'),
      import('@snowplow/browser-plugin-client-hints'),
    ]);

    this.trackerModule = trackerModule;

    const cfg = this.isFinnetMode() ? environment.snowplowPortal : environment.snowplow;
    const appId = this.isWebView() ? cfg.appIdMobile : cfg.appId;
    const consented = this.hasConsented(consent);

    this.callNewTracker(cfg.trackerName, cfg.endpoint, {
      appId,
      postPath: cfg.postPath,
      discoverRootDomain: true,
      cookieName: cfg.cookieName,
      cookieSameSite: 'Lax',
      crossDomainLinker: (linkElement) => {
        const networkSites = cfg.networkSites;
        return (
          linkElement.hostname !== location.hostname && networkSites.includes(linkElement.hostname)
        );
      },
      contexts: {
        webPage: true,
        session: true,
        browser: true,
      },
      stateStorageStrategy: 'cookieAndLocalStorage',
      plugins: [PerformanceNavigationTimingPlugin(), ClientHintsPlugin()],
      respectDoNotTrack: true,
      // A competitor click makes the parent app leave this page. keepalive lets a request
      // already on its way finish anyway; the callbacks tell trackBeforeLeaving() when the
      // collector has answered — a failure the tracker will retry isn't an answer yet (AI-646).
      keepalive: true,
      onRequestSuccess: () => this.settleRequestWaiters(),
      onRequestFailure: ({ willRetry }) => {
        if (!willRetry) this.settleRequestWaiters();
      },
      // Users who haven't given full-purpose consent get Snowplow's guest tracking:
      // https://docs.snowplow.io/docs/sources/web-trackers/anonymous-tracking/
      // Session stats still get collected, but with no persistent user identifier
      // and server-side IP anonymisation.
      anonymousTracking: consented
        ? undefined
        : { withSessionTracking: true, withServerAnonymisation: true },
    });

    // Add a global context for embedded SPA
    trackerModule.addGlobalContexts(
      [
        this.isConsentObject(consent) && {
          schema: 'iglu:net.finanzen/consent/jsonschema/2-0-0',
          data: consent,
        },
      ].filter(Boolean) as any,
    );

    // Dynamic context: evaluated at event fire time, applies to all events including page views
    trackerModule.addGlobalContexts([
      () => ({
        schema: 'iglu:net.finanzen/app_version/jsonschema/1-0-0',
        data: this.buildAppVersionContextData(),
      }),
    ]);

    // Identify user in Snowplow — only for users who gave full-purpose consent;
    // everyone else stays on the anonymousTracking config set above.
    if (token && consented) {
      const claims = this.parseJwt(token);
      const userId = claims?.['cognito:username'] || claims?.['username'] || claims?.['sub'];

      if (userId) {
        this.callSetUserId(userId);
      }
    }

    enableLinkClickTracking();
    this.isTrackerActive = true;

    // Set referrer manually for SPA
    const customReferrer = this.buildSpaPath();
    trackerModule.setReferrerUrl(customReferrer);
  }

  // Protected so tests can call it directly without intercepting addGlobalContexts.
  protected buildAppVersionContextData(): {
    version: number;
    test: { name: string; option: string };
  } {
    const flag = this.appVersion.v3FlagName();
    const variant = this.unleash.getVariant(flag);
    // Unleash returns { name: 'disabled', enabled: false } when no variant is configured;
    // fall back to appVersion.isV3() so that ?v3= URL overrides keep version and option in sync.
    let option: string;
    if (variant?.name && variant.name !== 'disabled') {
      option = variant.name;
    } else {
      option = this.appVersion.isV3() ? 'enabled' : 'disabled';
    }
    return { version: this.appVersion.isV3() ? 3 : 2, test: { name: flag, option } };
  }

  /** Parse the Cognito User id from JWT token */
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  }

  /** Track a page view */
  pageView(title?: string, referrer?: string): void {
    if (!this.isTrackerActive || !this.trackerModule) return;

    // Set custom referrer for SPA navigation
    if (referrer !== undefined) {
      const customReferrer = this.buildSpaPath();
      this.trackerModule.setReferrerUrl(
        referrer !== '' ? customReferrer + '/' + referrer : customReferrer,
      );
    }

    // Update document title if provided
    if (title) {
      this.pageTitle.setTitle(title);
    }

    // Actual page view tracking
    this.trackerModule.trackPageView(title ? { title } : undefined);
  }

  private sanitize(value?: string): string | undefined {
    return value?.replace(/;/g, '').trim();
  }

  /** Track certain events */
  track(event: AnalyticsEvent): void {
    if (!this.isTrackerActive || !this.trackerModule) return;

    this.trackerModule.trackStructEvent({
      category: this.sanitize(event.feature_name) || '',
      action: this.sanitize(event.event) || '',
      label: this.sanitize(event.element_title) || '',
      property: event.property ? JSON.stringify(event.property) : undefined,
    });
  }

  /**
   * For a click that makes the parent app leave this page (competitor rows): tracks the event
   * and resolves once the collector has answered, or after `maxWaitMs` at the latest, so the
   * caller can delay the navigation until the event is safely out. The answer may be to a
   * request sent just before ours; ours left right behind it, and keepalive covers that.
   * Resolves right away when tracking is off.
   */
  trackBeforeLeaving(event: AnalyticsEvent, maxWaitMs = 500): Promise<void> {
    if (!this.isTrackerActive || !this.trackerModule) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const settle = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(settle, maxWaitMs);
      this.requestSettledWaiters.push(settle);
      this.track(event);
    });
  }

  private settleRequestWaiters(): void {
    this.requestSettledWaiters.splice(0).forEach((settle) => settle());
  }
}
